'use client';

interface LegacyRainaHexagonBadgeProps {
  svgId: string;
}

/**
 * Hexagonal background for the Raina badge variant.
 * Renders a pointy-top hexagon with gradient fill, white stroke, and drop shadow.
 * ViewBox: 229x250 with hexagon shape from path.
 */
export function LegacyRainaHexagonBadge({ svgId }: LegacyRainaHexagonBadgeProps) {
  const id = (name: string) => `${svgId}${name}`;

  // Hexagon fill path from the provided SVG
  const hexFillPath =
    'M4.79468 59.1097C4.79468 52.7091 8.61337 46.9258 14.4996 44.4118L107.996 4.48076C112.005 2.76828 116.541 2.76828 120.55 4.48076L214.046 44.4118C219.933 46.9258 223.751 52.7091 223.751 59.1097V188.434C223.751 194.887 219.872 200.706 213.916 203.187L120.42 242.144C116.486 243.783 112.06 243.783 108.126 242.144L14.63 203.187C8.67419 200.706 4.79468 194.886 4.79468 188.434V59.1097Z';

  // Hexagon stroke path from the provided SVG
  const hexStrokePath =
    'M109.565 8.15527C112.572 6.87098 115.974 6.87093 118.981 8.15527L212.477 48.0859C216.892 49.9714 219.755 54.3091 219.756 59.1094V188.435C219.756 193.273 216.846 197.638 212.38 199.499L118.884 238.455C115.933 239.684 112.613 239.684 109.663 238.455L16.1667 199.499C11.7 197.638 8.78985 193.274 8.78979 188.435V59.1094C8.78992 54.309 11.6545 49.9714 16.0691 48.0859L109.565 8.15527Z';

  return (
    <svg aria-hidden="true" className="absolute inset-0 size-full" fill="none" preserveAspectRatio="xMidYMid meet" viewBox="0 0 229 250">
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="249.766"
          id={id('hexDropShadow')}
          width="228.546"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset dy="1.59822" />
          <feGaussianBlur stdDeviation="1.59822" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.0705882 0 0 0 0 0.0705882 0 0 0 0 0.0901961 0 0 0 0.06 0" />
          <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset dy="1.59822" />
          <feGaussianBlur stdDeviation="2.39734" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend in2="effect1_dropShadow" mode="normal" result="effect2_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect2_dropShadow" mode="normal" result="shape" />
        </filter>
        <linearGradient gradientUnits="userSpaceOnUse" id={id('hexBadgeGrad')} x1="114.273" x2="114.273" y1="1.7998" y2="244.705">
          <stop className="stop-color-raina-gradient-start" />
          <stop className="stop-color-raina-gradient-end" offset="1" />
        </linearGradient>
      </defs>
      <g filter={`url(#${id('hexDropShadow')})`}>
        <path d={hexFillPath} fill={`url(#${id('hexBadgeGrad')})`} />
        <path className="stroke-surface-background-0" d={hexStrokePath} strokeWidth="8" />
      </g>
    </svg>
  );
}

/**
 * Returns a CSS clip-path polygon for clipping content to the hexagon shape.
 * Uses the stroke path from RainaHexagonBadge which defines the visible border.
 * ViewBox: 229x250
 */
export function getHexagonClipPath(): string {
  // Key points from the stroke path (M109.565 8.15527... etc):
  // ViewBox is 229x250
  //
  // Top point: 114.27, 8.15 -> 49.9%, 3.3%
  // Top-right: 219.76, 59.1 -> 95.9%, 23.6%
  // Bottom-right: 219.76, 188.44 -> 95.9%, 75.4%
  // Bottom point: 114.27, 238.46 -> 49.9%, 95.4%
  // Bottom-left: 8.79, 188.44 -> 3.8%, 75.4%
  // Top-left: 8.79, 59.1 -> 3.8%, 23.6%
  return 'polygon(49.9% 3.3%, 95.9% 23.6%, 95.9% 75.4%, 49.9% 95.4%, 3.8% 75.4%, 3.8% 23.6%)';
}
